const PROJECT_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const DEVICE_LOCAL_MEDIA_KEYS = new Set([
    'blobid',
    'docblobid',
    'videoblobid',
    'localblobonly',
]);

const BINARY_CONTAINER_KEYS = new Set([
    'bytes',
    'buffer',
    'arraybuffer',
    'blob',
    'file',
    'docbytes',
    'pdfblob',
]);

const CANONICAL_BLOB_FIELDS = new Set([
    'id',
    'blob',
    'projectId',
    'sizeBytes',
    'mimeType',
    'storedAt',
]);

function normaliseMediaKey(value: unknown): string {
    return String(value ?? '').replace(/[_-]/g, '').toLowerCase();
}

export function isCanonicalProjectUuid(projectId: unknown): projectId is string {
    return typeof projectId === 'string' && PROJECT_UUID_RE.test(projectId);
}

/**
 * Locate the canonical backend Project that already owns a legacy Dexie id.
 * A duplicate mapping is an identity conflict, never a "pick the first row"
 * opportunity.
 */
export function findCanonicalProjectOwner(
    projects: unknown,
    legacyProjectId: unknown,
): Record<string, any> | null {
    const legacyId = typeof legacyProjectId === 'string' ? legacyProjectId.trim() : '';
    if (!legacyId || !Array.isArray(projects)) return null;
    const matches = projects.filter((project) => (
        project
        && typeof project === 'object'
        && isCanonicalProjectUuid((project as Record<string, any>).id)
        && String(
            (project as Record<string, any>).dexieId
            ?? (project as Record<string, any>).dexie_id
            ?? '',
        ) === legacyId
    ));
    const distinctIds = new Set(matches.map(project => String((project as Record<string, any>).id).toLowerCase()));
    if (distinctIds.size > 1) {
        const error = new Error('Legacy Project ownership is ambiguous; media storage was refused.') as Error & { code?: string };
        error.code = 'PROJECT_LEGACY_OWNER_AMBIGUOUS';
        throw error;
    }
    return matches[0] || null;
}

/**
 * True when a Project lane contains browser-profile-only bytes or locators.
 * Such values may be read or stored only under a canonical Project UUID.
 */
export function projectValueHasDeviceLocalMedia(value: unknown): boolean {
    const visited = new WeakSet<object>();
    const inspect = (candidate: unknown, parentKey = ''): boolean => {
        if (typeof candidate === 'string') {
            return /^\s*(?:blob:|data:)/i.test(candidate);
        }
        if (!candidate || typeof candidate !== 'object') return false;
        if (typeof Blob !== 'undefined' && candidate instanceof Blob) return true;
        if (typeof ArrayBuffer !== 'undefined'
            && (candidate instanceof ArrayBuffer || ArrayBuffer.isView(candidate))) return true;
        if (visited.has(candidate as object)) return false;
        visited.add(candidate as object);
        if (Array.isArray(candidate)) {
            if (BINARY_CONTAINER_KEYS.has(normaliseMediaKey(parentKey))
                && candidate.length > 0
                && candidate.every(entry => Number.isInteger(entry) && Number(entry) >= 0 && Number(entry) <= 255)) {
                return true;
            }
            return candidate.some(entry => inspect(entry, parentKey));
        }
        return Object.entries(candidate as Record<string, unknown>).some(([key, child]) => {
            const canonicalKey = normaliseMediaKey(key);
            if (DEVICE_LOCAL_MEDIA_KEYS.has(canonicalKey) && child !== null && child !== undefined && child !== false) {
                return true;
            }
            return inspect(child, key);
        });
    };
    return inspect(value);
}

export function requireCanonicalProjectUuid(projectId: unknown, operation: string): string {
    if (!isCanonicalProjectUuid(projectId)) {
        throw new TypeError(`${operation} requires a canonical project UUID`);
    }
    return projectId.toLowerCase();
}

export function omitCanonicalBlobFields(metadata: unknown): Record<string, unknown> {
    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return {};
    return Object.fromEntries(
        Object.entries(metadata).filter(([key]) => !CANONICAL_BLOB_FIELDS.has(key)),
    );
}
